import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import {
  ArrowRight,
  Building2,
  Database,
  FileCheck,
  HeartPulse,
  Layers3,
  LockKeyhole,
  MapPinned,
  ShieldCheck,
  Users,
} from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import Header from '@/components/layout/Header';
import MainLayout from '@/components/layout/MainLayout';

const privacyHighlights = [
  {
    title: 'Canadian Jurisdiction',
    description:
      'Deploy AI systems designed to keep sensitive workloads, documents, and models under Canadian legal and operational control.',
    icon: ShieldCheck,
  },
  {
    title: 'Provincial Alignment',
    description:
      'Support privacy requirements that vary across provinces, sectors, institutions, and public authorities.',
    icon: MapPinned,
  },
  {
    title: 'Regional Health Governance',
    description:
      'Configure AI environments around local health authority, hospital, clinic, or research network requirements.',
    icon: HeartPulse,
  },
  {
    title: 'Data Residency and Control',
    description:
      'Control where data is stored, where models run, and how sensitive information moves through the AI lifecycle.',
    icon: Database,
  },
  {
    title: 'Private Knowledge Bases',
    description:
      'Use organizational documents and datasets without turning private knowledge into shared model training data.',
    icon: LockKeyhole,
  },
  {
    title: 'Dedicated Deployment Options',
    description:
      'Support private, dedicated, on-premise, hybrid, or restricted-network deployments for sensitive environments.',
    icon: Layers3,
  },
];

const provincialRequirements = [
  'Provincial privacy legislation',
  'Regional health authority policies',
  'Institutional data governance rules',
  'Department-level access controls',
  'Research ethics and data-use requirements',
  'Data residency and hosting restrictions',
  'Audit, retention, and deletion requirements',
  'Role-based access and administrative boundaries',
];

const healthcareRequirements = [
  'Provincial healthcare privacy requirements',
  'Local health authority governance',
  'Hospital or clinic-specific data policies',
  'Research and clinical trial data controls',
  'Separation of datasets by department, project, or institution',
  'Restricted access for approved users and roles',
  'Secure handling of sensitive documents and knowledge bases',
  'Deployment models that reduce unnecessary data movement',
];

const governanceRequirements = [
  'Where data is stored',
  'Where models are trained',
  'Where inference is performed',
  'Who can access the system',
  'What data is used for training',
  'Whether customer data is retained',
  'Whether chats, documents, and files are used to improve shared models',
  'How deletion and retention policies are enforced',
  'How logs, embeddings, and generated outputs are handled',
];

const regulatedSectors = [
  'Healthcare',
  'Government',
  'Universities and research institutions',
  'Legal and professional services',
  'Defence and public safety',
  'Energy and utilities',
  'Industrial operations',
  'Financial and insurance services',
  'Indigenous and regional data initiatives',
];

const deploymentModels = [
  'Canadian-hosted cloud deployments',
  'Private tenant deployments',
  'Dedicated infrastructure deployments',
  'On-premise or customer-controlled environments',
  'Regional or institution-specific AI environments',
  'Air-gapped or restricted-network architectures where required',
  'Hybrid deployments that separate public, private, and sensitive workloads',
];

const sensitiveWorkloads = [
  'Public-sector information',
  'Healthcare and clinical data',
  'Research data',
  'Legal and compliance records',
  'Industrial and infrastructure data',
  'Indigenous, regional, or community-governed datasets',
  'Proprietary enterprise knowledge',
  'Sensitive operational workflows',
];

const Privacy = () => {
  return (
    <MainLayout>
      <Helmet>
        <title>Privacy - CanXP AI</title>
        <meta
          name="description"
          content="CanXP AI supports privacy-conscious AI deployments aligned with Canadian jurisdiction, provincial governance, institutional controls, and regulated-sector requirements."
        />
      </Helmet>
      <div className="bg-background text-foreground">
        <Header />
        <main className="container mx-auto px-4 py-12 md:py-24">
          <section className="mb-20 flex flex-col items-center justify-between gap-12 md:flex-row">
            <div className="md:w-1/2">
              <h1 className="mb-6 text-4xl font-bold leading-tight md:text-5xl">
                All the power of leading AI without leaving Canada
              </h1>
              <p className="mb-4 text-lg">
                For professionals who need to protect client trust, for Canadians who treat privacy as identity, and for students who want every advantage without the risk — this is your AI.
              </p>
              <p className="mb-6 text-lg">
                AI should make your life easier, not put your licence at risk, not betray your values, not drain your wallet in U.S. dollars.
              </p>
              <p className="mb-6 text-xl font-semibold text-primary">
                CanXP is Canada's sovereign AI platform.
              </p>
              <p className="text-lg">
                Built on Canadian infrastructure, compliant with Canadian law, and priced fairly for Canadians. The moment you use it, the compromise ends.
              </p>
            </div>
            <div className="flex justify-center md:w-1/2">
              <img
                src="/images/privacy/capebreton.jpg"
                alt="Canadian AI Infrastructure"
                className="h-auto max-w-full rounded-lg shadow-xl"
              />
            </div>
          </section>

          <Separator className="my-16 bg-primary/20" />

          <section className="mb-20">
            <div className="mx-auto max-w-4xl text-center">
              <Badge variant="outline" className="border-primary/25 bg-primary/5 text-primary">
                Privacy, Jurisdiction, and Canadian Data Control
              </Badge>
              <h2 className="mt-6 text-3xl font-bold tracking-tight md:text-5xl">
                Privacy built for Canadian AI sovereignty.
              </h2>
              <p className="mt-6 text-lg leading-8 text-muted-foreground md:text-xl">
                CanXP AI is built for organizations that cannot treat privacy as an afterthought. We support AI deployments where data residency, jurisdiction, access control, and operational governance matter.
              </p>
              <p className="mt-4 text-lg leading-8 text-muted-foreground">
                Whether you are a healthcare provider, university, government agency, enterprise, or regulated organization, CanXP AI gives you control over where your data lives, how it is used, and how your AI systems are governed.
              </p>
              <p className="mt-8 text-xl font-semibold text-primary md:text-2xl">Your data. Your jurisdiction. Your AI.</p>
            </div>
          </section>

          <section className="mb-20 grid grid-cols-1 gap-8 md:grid-cols-2 xl:grid-cols-3">
            {privacyHighlights.map((item) => {
              const Icon = item.icon;
              return (
                <Card
                  key={item.title}
                  className="border-primary/20 bg-card/80 shadow-[0_18px_60px_-40px_rgba(220,38,38,0.55)] transition-shadow duration-300 hover:shadow-[0_24px_70px_-38px_rgba(220,38,38,0.7)]"
                >
                  <CardContent className="p-6">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl border border-primary/20 bg-primary/10 text-primary">
                      <Icon className="h-5 w-5" />
                    </div>
                    <h3 className="mt-5 text-xl font-semibold tracking-tight">{item.title}</h3>
                    <p className="mt-3 text-sm leading-7 text-muted-foreground">{item.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </section>

          <section className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-[1.1fr_0.9fr]">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  Canadian jurisdiction by design
                </div>
                <h3 className="text-2xl font-semibold tracking-tight md:text-3xl">
                  Sovereign AI deployments built around Canadian legal and institutional requirements.
                </h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  CanXP AI supports sovereign AI deployments designed to keep sensitive workloads, documents, models, and operational data under Canadian jurisdiction.
                </p>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  For Canadian organizations, this means AI systems can be designed around Canadian legal, regulatory, and institutional requirements rather than forcing sensitive workflows into foreign-controlled cloud environments.
                </p>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  Our goal is simple: help organizations adopt advanced AI while maintaining control over where their data lives, who can access it, and how it is governed.
                </p>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <FileCheck className="h-3.5 w-3.5" />
                  Sensitive workloads
                </div>
                <p className="text-sm leading-7 text-muted-foreground">
                  This is especially important for organizations working with:
                </p>
                <ul className="mt-5 space-y-3 text-sm leading-7 text-muted-foreground">
                  {sensitiveWorkloads.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <MapPinned className="h-3.5 w-3.5" />
                  Provincial and regional requirements
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">Canadian privacy is not one-size-fits-all.</h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  Privacy obligations in Canada are not always uniform. A healthcare organization in one province may have different requirements than a healthcare organization in another province. Even within the same province, different health authorities, hospital networks, research institutions, and regional service providers may operate under different policies, approval processes, and data-handling rules.
                </p>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  CanXP AI is designed to support these differences and align deployments with the jurisdictional and operational requirements that apply to the organization using them.
                </p>
                <ul className="mt-6 grid gap-3 text-sm leading-7 text-muted-foreground sm:grid-cols-2">
                  {provincialRequirements.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <HeartPulse className="h-3.5 w-3.5" />
                  Healthcare privacy and local control
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">Healthcare is one of the clearest examples of why jurisdiction matters.</h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  Patient information, clinical workflows, medical research, and operational health data are often governed by provincial legislation, regional health authority policies, hospital-specific rules, and institutional review processes. A single national AI deployment model is often not appropriate for these environments.
                </p>
                <ul className="mt-6 grid gap-3 text-sm leading-7 text-muted-foreground">
                  {healthcareRequirements.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-[0.95fr_1.05fr]">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <Database className="h-3.5 w-3.5" />
                  Beyond residency
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">Data residency is only one part of privacy.</h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  Keeping data in Canada is important, but it is not the entire privacy story. True AI privacy also requires control over the full AI lifecycle.
                </p>
                <ul className="mt-6 space-y-3 text-sm leading-7 text-muted-foreground">
                  {governanceRequirements.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-gradient-to-br from-primary/10 via-card/80 to-card/80">
              <CardContent className="p-8 md:p-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-background/70 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <LockKeyhole className="h-3.5 w-3.5" />
                  Trust statement
                </div>
                <h3 className="text-3xl font-semibold tracking-tight md:text-4xl">Your data is not our training product.</h3>
                <p className="mt-5 text-base leading-8 text-muted-foreground md:text-lg">
                  CanXP AI is designed for organizations that need confidence that their private knowledge remains private. Customer documents, chats, datasets, and institutional knowledge should not be treated as raw material for a shared public model.
                </p>
                <div className="mt-8 grid gap-4 sm:grid-cols-2">
                  <div className="rounded-2xl border border-primary/15 bg-background/70 p-5">
                    <p className="text-sm font-medium">Your data remains under your control</p>
                  </div>
                  <div className="rounded-2xl border border-primary/15 bg-background/70 p-5">
                    <p className="text-sm font-medium">Your documents are not used to train unrelated customer systems</p>
                  </div>
                  <div className="rounded-2xl border border-primary/15 bg-background/70 p-5">
                    <p className="text-sm font-medium">Your AI environment can be isolated by organization, project, or deployment</p>
                  </div>
                  <div className="rounded-2xl border border-primary/15 bg-background/70 p-5">
                    <p className="text-sm font-medium">Your deletion and retention requirements can be reflected in deployment design</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>

          <section className="mb-20 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <Building2 className="h-3.5 w-3.5" />
                  Built for regulated environments
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">Designed for organizations that need more than a generic AI chatbot.</h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  We support privacy-conscious AI systems for sectors where governance, jurisdiction, and operational trust are essential.
                </p>
                <div className="mt-6 flex flex-wrap gap-3">
                  {regulatedSectors.map((sector) => (
                    <span
                      key={sector}
                      className="rounded-full border border-border/70 bg-background/70 px-4 py-2 text-sm text-muted-foreground"
                    >
                      {sector}
                    </span>
                  ))}
                </div>
              </CardContent>
            </Card>

            <Card className="border-primary/15 bg-card/70">
              <CardContent className="p-8">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <Layers3 className="h-3.5 w-3.5" />
                  Flexible deployment models
                </div>
                <h3 className="text-2xl font-semibold tracking-tight">Choose the right balance of performance, cost, control, and compliance.</h3>
                <p className="mt-4 text-base leading-8 text-muted-foreground">
                  Different organizations have different privacy requirements. CanXP AI can support deployment strategies based on the sensitivity of the workload.
                </p>
                <ul className="mt-6 space-y-3 text-sm leading-7 text-muted-foreground">
                  {deploymentModels.map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 rounded-full bg-primary" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </section>

          <section className="mb-12">
            <Card className="border-primary/15 bg-card/60">
              <CardContent className="p-8 md:p-10">
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  <Users className="h-3.5 w-3.5" />
                  Practical note
                </div>
                <p className="text-sm leading-7 text-muted-foreground md:text-base">
                  CanXP AI provides privacy-conscious AI infrastructure and deployment options designed to support customer governance requirements. Customers remain responsible for determining the legal, regulatory, contractual, and institutional obligations that apply to their specific use case. CanXP AI works with customers and their advisors to align technical deployment models with those requirements.
                </p>
              </CardContent>
            </Card>
          </section>

          <section className="pb-4 pt-8">
            <Card className="overflow-hidden border-primary/20 bg-gradient-to-br from-primary/10 via-background to-background shadow-[0_24px_120px_-72px_rgba(220,38,38,0.9)]">
              <CardContent className="p-8 md:p-12">
                <div className="mx-auto max-w-4xl text-center">
                  <Badge variant="outline" className="border-primary/25 bg-background/70 text-primary">
                    Sovereign AI privacy consultation
                  </Badge>
                  <h2 className="mt-6 text-3xl font-bold tracking-tight md:text-5xl">
                    Build AI within your governance boundaries.
                  </h2>
                  <p className="mt-6 text-lg leading-8 text-muted-foreground">
                    CanXP AI helps organizations deploy AI systems that respect Canadian jurisdiction, provincial privacy rules, regional governance, and institutional data-control requirements.
                  </p>
                  <p className="mt-4 text-lg leading-8 text-muted-foreground">
                    If your organization needs AI that can operate within strict privacy, residency, or compliance constraints, we can help design the right deployment model.
                  </p>
                  <div className="mt-8 flex justify-center">
                    <Button asChild size="lg" className="gap-2">
                      <Link to="/enterprise">
                        Book a Sovereign AI Privacy Consultation
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </section>
        </main>
      </div>
    </MainLayout>
  );
};

export default Privacy;
